import type { AxiosError } from "axios";
import type { MessageTypes } from "../types/message";

export function getMessageTypeFromMime(mimeType: string): MessageTypes {
  if (mimeType.startsWith("text/")) return "text";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("video/")) return "video";
  return "file";
}

export function getApiErrorMessage(err: unknown): string {
  const axiosErr = err as AxiosError<{
    message?: string;
    error?: string;
  }>;

  return (
    axiosErr.response?.data?.message ||
    axiosErr.response?.data?.error ||
    "Tente novamente mais tarde."
  );
}

export const months = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export function formatHour(date?: Date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

export function formatDateTime(date?: Date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(new Date(date));
}

export function normalizePhone(value: string) {
  return value.replace(/\D/g, "");
}

export function normalizeDigits(value: string) {
  return value.replace(/\D/g, "");
}

export function formatZipCode(value: string) {
  const digits = normalizeDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export const COUNTRY_CODE_OPTIONS = [
  { value: "55", label: "Brasil (+55)" },
  { value: "1", label: "Estados Unidos (+1)" },
  { value: "351", label: "Portugal (+351)" },
  { value: "34", label: "Espanha (+34)" },
  { value: "44", label: "Reino Unido (+44)" },
];

const DEFAULT_COUNTRY_CODE = COUNTRY_CODE_OPTIONS[0].value;
const LOCAL_PHONE_MIN_LENGTH = 10;
const LOCAL_PHONE_MAX_LENGTH = 11;
const KNOWN_COUNTRY_CODES = COUNTRY_CODE_OPTIONS.map(
  (option) => option.value,
).sort((a, b) => b.length - a.length);

function stripCountryCodeIfPresent(digits: string, countryCode: string) {
  if (!countryCode || !digits.startsWith(countryCode)) {
    return null;
  }

  const localDigits = digits.slice(countryCode.length);
  if (
    localDigits.length < LOCAL_PHONE_MIN_LENGTH ||
    localDigits.length > LOCAL_PHONE_MAX_LENGTH
  ) {
    return null;
  }

  return localDigits;
}

function inferCountryCodeAndLocalDigits(
  digits: string,
  forceInference = false,
): { countryCode: string; localDigits: string } | null {
  if (!forceInference && digits.length <= LOCAL_PHONE_MAX_LENGTH) {
    return null;
  }

  for (const knownCountryCode of KNOWN_COUNTRY_CODES) {
    const localDigits = stripCountryCodeIfPresent(digits, knownCountryCode);
    if (localDigits) {
      return { countryCode: knownCountryCode, localDigits };
    }
  }

  return null;
}

function resolveLocalDigits(value: string, countryCode?: string) {
  const digits = normalizePhone(value);
  if (!digits) return "";

  const normalizedCountryCode = normalizePhone(countryCode || "");
  const forcedByPlusPrefix = value.trim().startsWith("+");

  if (normalizedCountryCode) {
    const stripped = stripCountryCodeIfPresent(digits, normalizedCountryCode);
    if (stripped) {
      return stripped;
    }
  }

  const inferred = inferCountryCodeAndLocalDigits(digits, forcedByPlusPrefix);
  if (inferred) {
    return inferred.localDigits;
  }

  return digits;
}

function formatPhoneBody(digits: string) {
  if (!digits) return "";
  if (digits.length <= 2) return digits;

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length <= 4) {
    return `(${ddd}) ${rest}`;
  }

  const splitIndex = Math.max(4, rest.length - 4);
  const first = rest.slice(0, splitIndex);
  const second = rest.slice(splitIndex);

  if (!second) return `(${ddd}) ${first}`;

  return `(${ddd}) ${first}-${second}`;
}

export function formatLocalPhone(value: string, countryCode?: string) {
  const digits = resolveLocalDigits(value, countryCode);
  return formatPhoneBody(digits);
}

export function formatPhoneWithCountryCode(
  value: string,
  countryCode?: string,
) {
  const digits = normalizePhone(value);
  if (!digits) return "";

  const normalizedCountryCode = normalizePhone(countryCode || "");

  if (normalizedCountryCode) {
    const localDigits =
      stripCountryCodeIfPresent(digits, normalizedCountryCode) ?? digits;
    const phoneBody = formatPhoneBody(localDigits);
    if (!phoneBody) return "";
    return `+${normalizedCountryCode} ${phoneBody}`;
  }

  const inferred = inferCountryCodeAndLocalDigits(
    digits,
    value.trim().startsWith("+"),
  );

  if (inferred) {
    const phoneBody = formatPhoneBody(inferred.localDigits);
    if (!phoneBody) return "";
    return `+${inferred.countryCode} ${phoneBody}`;
  }

  const phoneBody = formatPhoneBody(digits);
  if (!phoneBody) return "";
  return `+${DEFAULT_COUNTRY_CODE} ${phoneBody}`;
}

type UserPhoneFields = {
  countryCode?: string | null;
  dialCode?: string | null;
};

export function formatUserPhone({ countryCode, dialCode }: UserPhoneFields) {
  const normalizedCountryCode = normalizePhone(countryCode || "");
  const normalizedDialCode = normalizePhone(dialCode || "");

  if (!normalizedCountryCode || !normalizedDialCode) {
    return "";
  }

  return formatPhoneWithCountryCode(normalizedDialCode, normalizedCountryCode);
}
